<template>
    <div class="-m-5 md:m-0 h-full flex-1 overflow-hidden">
        <tabulator
            :columns="columns"
            :loading="tableIsLoading"
            @search="search"
            ajax-path="pbxware/dids"
            :ajax-params="{tenant_id: $route.meta.serverId}"
        />
        <app-modal
            :show.sync="showEditModal"
            width="md:w-1/2 lg:w-1/4"
            @close="editModalClosed"
        >
            <template slot="header">Edit DID</template>
            <template slot="body">                
                <app-input
                    v-model="editForm.name"
                    placeholder="Group Name"
                    :error="editForm.errors.get('name')"
                    @updated="editForm.errors.clear('name')"
                    focus
                    required
                />
                <app-input
                    v-model="editForm.ext"
                    placeholder="Extension"
                    :error="editForm.errors.get('ext')"
                    @updated="editForm.errors.clear('ext')"
                    required
                    :limit="3"
                    integer
                />
                <app-select
                    v-model="editForm.extensions"
                    placeholder="Destinations"
                    :error="editForm.errors.get('extensions')"
                    @updated="editForm.errors.clear('extensions')"
                    :options="extensions"
                    value-key="ext"
                    required
                    multiple
                />
                <app-input
                    v-model="editForm.max_limit"
                    placeholder="Incoming Limit (per call)"
                    :error="editForm.errors.get('max_limit')"
                    @updated="editForm.errors.clear('max_limit')"
                    :limit="2"
                    integer
                />
                <div class="mt-5 flex items-center justify-end">
                    <app-button 
                        class="btn-white" 
                        @click="showEditModal = false"
                        :disable="editFormIsLoading"
                    >Cancel</app-button>
                    <app-button 
                        class="btn-blue ml-3" 
                        @click="store"
                        :loading="editFormIsLoading"
                    >Save</app-button>
                </div>
            </template>
        </app-modal>
        <app-modal
            :show.sync="showAddModal"
            width="md:w-1/2 lg:w-1/4"
            @close="addModalClosed"
        >
            <template slot="header">Add DID</template>
            <template slot="body">
                <div>
                    <app-input
                        v-model="addForm.name"
                        placeholder="Group Name"
                        :error="addForm.errors.get('name')"
                        @updated="addForm.errors.clear('name')"
                        focus
                        required
                    />
                    <app-input
                        v-model="addForm.ext"
                        placeholder="Extension"
                        :error="addForm.errors.get('ext')"
                        @updated="addForm.errors.clear('ext')"
                        required
                        :limit="3"
                        integer
                    />
                    <app-select
                        v-model="addForm.extensions"
                        placeholder="Destinations"
                        :error="addForm.errors.get('extensions')"
                        @updated="addForm.errors.clear('extensions')"
                        :options="extensions"
                        value-key="ext"
                        required
                        multiple
                    />
                    <app-input
                        v-model="addForm.max_limit"
                        placeholder="Incoming Limit (per call)"
                        :error="addForm.errors.get('max_limit')"
                        @updated="addForm.errors.clear('max_limit')"
                        :limit="2"
                        integer
                    />
                    <div class="mt-5 flex items-center justify-end">
                        <app-button 
                            class="btn-white" 
                            @click="showAddModal = false"
                            :disable="addFormIsLoading"
                        >Cancel</app-button>
                        <app-button 
                            class="btn-blue ml-3" 
                            @click="store"
                            :loading="addFormIsLoading"
                        >Add</app-button>
                    </div>
                </div>
            </template>
        </app-modal>
    </div>
</template>

<script>
    import { mapGetters } from 'vuex';
    import Form from '@/helpers/Form';
    export default {
        data() {
            return {
                addForm: new Form({
                    name: null,
                    ext: null,
                    extensions: [],
                    max_limit: 1,
                }),
                editForm: new Form({
                    name: null,
                    ext: null,
                    extensions: [],
                    max_limit: 1,
                }),
                editFormIsLoading: false,
                addFormIsLoading: false,
                tableIsLoading: false,
                showEditModal: false,
                showAddModal: false,
                generalInputIsLoading: false,
                columns: [
                    {
                        title: 'DID / Channel',
                        field: 'number',
                        sorter: 'string',
                        minWidth: 150,
                    },
                    {
                        title: 'Extension',
                        field: 'ext',
                        sorter: 'string',
                        minWidth: 200,
                    },
                    {
                        title: 'Trunk',
                        field: 'trunk',
                        sorter: 'string',
                        minWidth: 150,
                    },
                    {
                        title: 'Type',
                        field: 'type',
                        sorter: 'string',
                        minWidth: 150,
                    },
                    {
                        title: 'Status',
                        field: 'status',
                        sorter: 'string',
                        minWidth: 130,
                    }
                ],
                actions: [
                    {
                        label: 'Edit',
                        icon: () => import('@/icons/EditOutlined.vue'),
                        requiredSelect: true,
                        handler: dids => {
                            this.edit(dids[0].id)
                        },
                    },
                    {
                        label: 'Delete',
                        icon: () => import('@/icons/DeleteOutlined.vue'),
                        requiredSelect: true,
                        handler: dids => {
                            this.deleteItem(dids[0]);
                        }
                    },
                    {
                        label: 'Refresh',
                        icon: () => import('@/icons/Refresh.vue'),
                        handler: this.getDids,
                    },
                    {
                        label: 'Add',
                        icon: () => import('@/icons/Add.vue'),
                        handler: this.add,
                    }
                ],
            }
        },
        computed: {
            ...mapGetters({
                items: 'pbxware/dids/items',
                item: 'pbxware/dids/item',
                extensions: 'pbxware/dids/extensions',
            }),
        },
        mounted() {
            this.getDids();
        },
        methods: {            
            getDids() {
                this.tableIsLoading = true;
                this.$store.dispatch('pbxware/dids/index')
                    .then(() => {
                        this.tableIsLoading = false;
                    })
                    .catch(() => {
                        this.tableIsLoading = false;
                    });
            },
            edit(id) {
                this.showEditModal = true;
                this.generalInputIsLoading = true;
                this.$store.dispatch('pbxware/dids/edit', id)
                    .then(({ data }) => {
                        this.generalInputIsLoading = false;
                        this.editForm.set({id});
                        this.editForm.set(data['general']);
                    })
                    .catch(() => {
                        this.generalInputIsLoading = false;
                    })
            },
            editModalClosed() {
                this.editForm.reset();
                this.editForm.errors.clear();                
            },
            update() {
                this.editFormIsLoading = true;
                this.$store.dispatch('pbxware/dids/update', this.editForm.payload())
                    .then(() => {
                        this.editFormIsLoading = false;
                        this.showEditModal = false;
                        this.getDids();
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'Configuration saved successfully.'
                        })
                    })
                    .catch(({ response }) => {
                        this.editFormIsLoading = false;
                        this.editForm.errors.record(response.data);

                        if (this.editForm.errors.has('error')) {
                            this.$swal.fire({
                                type: 'error',
                                title: 'Oops!',
                                text: this.editForm.errors.get('error')
                            });
                        } else {
                            this.$swal.fire({
                                type: 'error',
                                title: 'Oops!',
                                text: response.data.message
                            });
                        }
                    });
            },
            addModalClosed() {
                this.addForm.reset();
                this.addForm.errors.clear();
            },
            add() {
                this.showAddModal = true;
            },
            store() {
                this.addFormIsLoading = true;
                this.$store.dispatch('pbxware/dids/store', this.addForm.payload())
                    .then(() => {
                        this.addFormIsLoading = false;
                        this.showAddModal = false;
                        this.getDids();
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'Ring group was added successfully.'
                        })
                    })
                    .catch(({ response }) => {
                        this.addFormIsLoading = false;
                        this.addForm.errors.record(response.data);
                    })
            },
            deleteItem(item) {
                this.$swal.fire({
                    type: 'warning',
                    title: 'Are you sure?',
                    html: `You are about to delete "<strong>${item.name}</strong>" ring group.`,
                    showCancelButton: true,
                    confirmButtonColor: '#f56565',
                    confirmButtonText: 'Yes, delete it!',
                    showLoaderOnConfirm: true,
                    preConfirm: (response) => {
                        return this.$store.dispatch('pbxware/dids/delete', item.id);
                    },
                    allowOutsideClick: () => !this.$swal.isLoading()
                })
                .then((result) => {
                    if (result.value) {
                        this.getDids();
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'Ring group was successfully deleted.'
                        })
                    }
                })
                
            },
            search() {

            }
        }
    }
</script>

<style lang="scss" scoped>
    .edit-form {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
        grid-gap: theme('spacing.5');
    }
</style>